import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useReducer, useCallback, useEffect } from 'react';

import analytics from 'services/analytics';
import useStudioBackButton from './useStudioBackButton';
import { getProjects, onChangeSpace } from './ProjectAPI';
import {
  selectHotspotProduct,
  toggleBannerZindex,
  getIsSharedSpace
} from 'redux/Studio';
import { logoutUser } from 'redux/User/actions';
import { SHARED_VIEW_TYPE } from 'utils/constants';
import {
  customerLandingPage,
  navigateToLoginPage,
  navigateToStartNewSpace,
  navigateToStudioCartFor,
  navigateToTalkableDashboard,
  navigateToShopCart
} from 'services/routing';
import {
  clickOutsideDropdownMenuSmall,
  drawerOverlayClicked,
  dropdownMenuSmallClicked,
  hamburgerToggleClicked,
  listItemClicked,
  listItemSublevelClicked,
  listItemWithButtonClicked,
  modalButtonClicked,
  modalLinkClicked,
  modsyHeartLogoClicked,
  modsyLogoClicked,
  navigationModalClose,
  navItemWithIconClicked,
  primaryListItemClicked,
  secondaryListItemClicked
} from 'web-ui-kit/events';
import { fetchShoppingCart } from 'shared/components/Checkout/state';
import useZendesk,
{
  emitUpdateZendeskHelpPosition,
  setEnv,
  zendeskAvailable
} from 'web-services/chat';

export default function useHeaderMonarch ({
  allConversations = {},
  allSpaces = [],
  design = {},
  dispatchSetLayoutModalActive = () => {},
  feShopRewriteWebPPEnabled = false,
  feZendeskChatbot = false,
  history = {},
  isAdmin = false,
  isCurrentStudioReady = false,
  isProspect = false,
  layoutModalActive = false,
  openZendesk = () => {},
  reduxDispatcher = () => {},
  spaceId = null,
  studio = {},
  userId = null
} = {}) {
  const [state, internalDispatcher] = useReducer(internalReducer, internalReducer());

  const { helpModalOpen } = state;

  const designId = (design && design.id()) || null;
  const { backButtonConfig } = useStudioBackButton({ designId, dispatchSetLayoutModalActive, history, spaceId, layoutModalActive });
  const projects = getProjects({ allConversations, allSpaces });

  function getIsLoggedOutSharedMode () {
    // We only want a share mode for logged out users.
    const shareMode = getIsSharedSpace();
    return shareMode && !userId;
  }

  function onSpaceChange () {
    // Make sure selected product gets cleared when we change spaces.
    isCurrentStudioReady && reduxDispatcher(selectHotspotProduct(null, null));
  }
  // Update the Zendesk widget env settings
  setEnv({ appEnv: process.env.REACT_APP_ENV });
  // Handles loading zendesk and handling zendesk widget
  useZendesk({ feZendeskChatbot });

  function handleCartClick () {
    trackHeaderEvent('CART');
    if (isCurrentStudioReady) {
      const shareMode = getIsSharedSpace();
      const viewType = shareMode ? SHARED_VIEW_TYPE : '';
      navigateToStudioCartFor({ spaceId, designId, viewType });
    } else {
      navigateToShopCart();
    }
  }

  function handleHelpClick () {
    internalDispatcher({ type: 'open-help-modal' });
  }

  const openHelpMenuLink = useCallback(() => {
    if (feZendeskChatbot && zendeskAvailable()) {
      emitUpdateZendeskHelpPosition();
      openZendesk();
    } else {
      trackHeaderEvent('Help');
      const newWindow = window.open();
      newWindow.opener = null;
      newWindow.location = 'https://modsy.zendesk.com/hc/en-us';
    }
  }, [feZendeskChatbot, trackHeaderEvent]);

  const onNavigationModalButtonClick = useCallback(({ payload }) => {
    const { buttonId: modalButtonId } = payload;
    switch (modalButtonId) {
      case 'get-in-touch-button':
        openHelpMenuLink();
        break;
      default:
        break;
    }
  }, [openHelpMenuLink]);

  const onListItemWithButtonPressed = useCallback(({ payload }) => {
    const { text } = payload;
    switch (text) {
      case 'Get In Touch':
        openHelpMenuLink();
        break;
      default:
        break;
    }
  }, [openHelpMenuLink]);

  const dispatch = ({ type, payload }) => {
    const { text, href, route } = payload || {};
    switch (type) {
      case modsyLogoClicked:
      case modsyHeartLogoClicked:
        trackHeaderEvent('Modsy logo');
        break;

      case listItemClicked:
      case listItemSublevelClicked:
        if ((/logout/i).test(text)) {
          reduxDispatcher(logoutUser());
        }

        if ((/Start New Project/i).test(text)) {
          const partnerSlug = studio && studio.partnerDetails() && get(studio.partnerDetails(), 'slug', '');
          navigateToStartNewSpace({ partnerSlug });
          break;
        }

        if (route && (/modsy.zendesk.com/i).test(route.destination)) {
          const newWindow = window.open();
          newWindow.opener = null;
          newWindow.location = route.destination;
          break;
        }

        if (payload.isUserProjectListItem) {
          const {
            isLayoutSpace,
            isShowroomSpace,
            spaceStatus,
            spaceId
          } = payload.projectDetails;

          const analyticsInfo = {
            design,
            studio,
            trackSpaceSelectorNavigate
          };

          onChangeSpace({
            analyticsInfo,
            isAdmin,
            isLayoutSpace,
            isShowroomSpace,
            onSpaceChange,
            spaceId,
            spaceStatus,
            userId
          });
        }
        break;

      case listItemWithButtonClicked:
        onListItemWithButtonPressed({ payload });
        break;

      case dropdownMenuSmallClicked:
      case hamburgerToggleClicked:
      case drawerOverlayClicked:
      case clickOutsideDropdownMenuSmall:
        if (isCurrentStudioReady) {
          internalDispatcher({ type, payload });
          reduxDispatcher(toggleBannerZindex(state.showMobileHeaderElementsAboveStudio));
        }
        break;

      case primaryListItemClicked:
        if (href === '/shop' && feShopRewriteWebPPEnabled) {
          window.location.href = href;
        } else {
          history.push({
            pathname: href || '',
            search: window.location.search
          });
        }
        break;

      case secondaryListItemClicked:
        // Order history is hosted on nextjs app.
        if ((/order history/i).test(text)) {
          window.location.href = href;
          break;
        }

        // Help is hosted on a different app.
        // It requires a full page refresh.
        if ((/help/i).test(text)) {
          window.location.href = href;
          break;
        }

        if ((/referral/i).test(text)) {
          navigateToTalkableDashboard();
          break;
        }

        if ((/logout/i).test(text)) {
          reduxDispatcher(logoutUser());
          break;
        }

        if ((/login/i).test(text)) {
          navigateToLoginPage();
          break;
        }

        if ((/Start New Project/i).test(text)) {
          const partnerSlug = studio && studio.partnerDetails() && get(studio.partnerDetails(), 'slug', '');
          navigateToStartNewSpace({ partnerSlug });
          break;
        }

        history.push({
          pathname: href || '',
          search: window.location.search
        });
        break;

      case navigationModalClose:
        internalDispatcher({ type, payload });
        break;

      case modalLinkClicked:
        internalDispatcher({ type, payload });
        break;

      case modalButtonClicked:
        onNavigationModalButtonClick({ payload });
        internalDispatcher({ type, payload });
        break;

      case navItemWithIconClicked:
        // Requirement: open help links in a different tab
        if ((/help/i).test(text)) {
          const newWindow = window.open();
          newWindow.opener = null;
          newWindow.location = route.destination;
          break;
        }

        console.log({ type, payload });
        break;
      default:
        console.log({ type, payload });
        break;
    }
  };

  // Run initialization side effects when the component mounts.
  useEffect(() => {
    initialize({ dispatch: reduxDispatcher, isCurrentStudioReady });
  }, []);

  /**
   * Methods related to analytics
   */
  function trackHeaderEvent (name, extraData) {
    let data = { 'Link Name': name };
    if (!isEmpty(extraData)) {
      data = { ...data, extraData };
    }
    reduxDispatcher(analytics.trackEvent({ event: 'Website Header Click', data }));
  }

  function trackSpaceSelectorNavigate ({ studio, design, userId, spaceIdSelected }) {
    reduxDispatcher(analytics.trackSpaceSelectorNavigate({ studio, design, userId, spaceIdSelected }));
  }

  const modsyButtonConfig = {
    destination: !isProspect ? customerLandingPage : '/',
    app: !isProspect ? 'nextjs' : 'web-post-purchase'
  };

  return {
    /**
     * The header nav back button configuration
     * passed down to the header nav component.
     */
    backButtonConfig,

    /**
     * The header modsy logo configuration
     * passed down to the header nav component.
     */
    modsyButtonConfig,

    /**
    * The global dispatch that handles all use cases.
    */
    dispatch,

    /**
     * A logged out user is viewing a shared studio
     */
    isLoggedOutSharedMode: getIsLoggedOutSharedMode(),

    /**
    * Fire this function to open the checkout page.
    */
    handleCartClick,

    /**
     * Fire this function to open the help page in a new tab.
     */
    handleHelpClick,

    /**
     * Whether the help modal is open
     */
    helpModalOpen,

    /**
    * All projects for a given user.
    */
    projects
  };
}

/**************************************************
 * Logic for internal state transitions.
 **************************************************/
const initialState = {
  showMobileHeaderElementsAboveStudio: false,
  helpModalOpen: false
};

function internalReducer (state = initialState, { type, payload } = {}) {
  switch (type) {
    case dropdownMenuSmallClicked:
    case hamburgerToggleClicked:
    case drawerOverlayClicked:
    case clickOutsideDropdownMenuSmall:
      return {
        ...state,
        showMobileHeaderElementsAboveStudio: !state.showMobileHeaderElementsAboveStudio
      };
    case navigationModalClose:
    case modalButtonClicked:
    case modalLinkClicked:
      return {
        ...state,
        helpModalOpen: false
      };
    case 'open-help-modal':
      return {
        ...state,
        helpModalOpen: true
      };

    default:
      return state;
  }
}

/*********************
* Side Effects.
*********************/

function initialize ({
  dispatch,
  isCurrentStudioReady
} = {}) {
  if (isCurrentStudioReady) {
    // Set BannerContainer z-index to 0
    // when component mounts to ensure Design title stays
    // on top of banners. [phones]
    dispatch(toggleBannerZindex(true));
  } else {
    // Fetch the logged in user's cart.
    // This is needed to populate the item count in the cart buttons.
    // Ignore fetch errors here as we would fetch again when the Checkout
    // component is loaded and there is not really a good place to display
    // an error at this point. Also, cart fetch errors are already bugsnaged
    // by the warehouse service.
    dispatch(fetchShoppingCart()).then().catch();
  }
}
